import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useProject } from "../ProjectContext";
import { X } from "lucide-react";
import { create_project_api, fetch_projects_api } from "../api/project.api";

const ProjectDashboard = () => {
  const { user, setRedirectPath, userId } = useAuth();
  const { selectProject } = useProject();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal controls
  const [showModal, setShowModal] = useState(false);
  const [projName, setProjName] = useState("");
  const [projDesc, setProjDesc] = useState("");
  const [modalError, setModalError] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) {
      setRedirectPath("/project");
      navigate("/login");
      return;
    }
    fetchProjects();
    selectProject(null);
    // eslint-disable-next-line
  }, [user]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch_projects_api(userId);
      if (res.projects) setProjects(res.projects);
      else setProjects([]);
    } catch (err) {
      setProjects([]);
    }
    setLoading(false);
  };

  const openModal = () => {
    setProjName("");
    setProjDesc("");
    setModalError("");
    setShowModal(true);
  };

  const handleCreateProject = async () => {
    if (!projName.trim() || !projDesc.trim()) {
      setModalError("All fields are required.");
      return;
    }
    setCreating(true);
    try {
      const res = await create_project_api(userId, projName.trim(), projDesc.trim());
      if (res.project) {
        selectProject(res.project);
        setShowModal(false);
        setCreating(false);
        fetchProjects();
        navigate("/project/landing");
      } else {
        setModalError(res.message || "Project creation failed.");
        setCreating(false);
      }
    } catch (err) {
      setModalError("Project creation failed (network/server error).");
      setCreating(false);
    }
  };

  const handleOpenProject = (project) => {
    selectProject(project);
    navigate(`/project/landing`);
  };

  return (
    <>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2340]">
            Project Dashboard
          </h1>
          
          <button 
            onClick={openModal}
            className="w-full sm:w-auto px-6 sm:px-9 py-2.5 sm:py-3 bg-[#ff2e2e] text-white font-bold text-sm sm:text-base md:text-lg rounded-xl shadow-[0_6px_22px_rgba(255,46,46,0.16)] hover:bg-[#e60000] transition-all duration-200 transform hover:scale-105"
          >
            + Create Project
          </button>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="my-8 text-[#888] text-center">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="my-8 text-[#888] text-center">
            No projects found. Create your first project!
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full">
            {projects.map((proj, idx) => (
              <button
                key={proj.folder || idx}
                onClick={() => handleOpenProject(proj)}
                className="w-full min-h-32 block bg-white rounded-xl sm:rounded-2xl border-none shadow-[0_6px_18px_rgba(20,20,40,0.09)] p-4 sm:p-6 text-left cursor-pointer transition-all duration-200 hover:shadow-[0_12px_34px_rgba(255,46,46,0.19)] hover:-translate-y-0.5 hover:scale-[1.025] focus:outline-none"
              >
                <div className="text-base sm:text-lg font-bold text-[#f53838] mb-2 break-words">{proj.projectName}</div>
                <div className="text-sm sm:text-base text-[#31344d] break-words">{proj.projectDescription}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-[rgba(26,34,64,0.18)] z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-[0_12px_48px_rgba(30,30,60,0.20)] p-6 sm:p-10 max-w-[420px] w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-[1.35rem] font-bold text-[#ff2e2e]">
                Create New Project
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1 transition-colors"
                disabled={creating}
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex flex-col mb-4">
              <label className="font-semibold mb-1.5 text-[#272c3a]">Project Name</label>
              <input
                className="border border-[#d8d8d8] rounded-lg px-3.5 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#ff2e2e]"
                value={projName}
                onChange={(e) => setProjName(e.target.value)}
                placeholder="Project Title"
              />
            </div>

            <div className="flex flex-col mb-4">
              <label className="font-semibold mb-1.5 text-[#272c3a]">Project Description</label>
              <textarea
                className="border border-[#d8d8d8] rounded-lg px-3.5 py-2.5 text-base min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-[#ff2e2e]"
                value={projDesc}
                onChange={(e) => setProjDesc(e.target.value)}
                placeholder="Short description"
              />
            </div>

            {modalError && <div className="text-[#d40000] mb-2 text-sm">{modalError}</div>}

            <div className="flex flex-col sm:flex-row gap-3.5 mt-2">
              <button
                onClick={handleCreateProject}
                disabled={creating}
                className="bg-[#ff2e2e] text-white font-bold px-6 py-2.5 rounded-lg text-base hover:bg-[#e60000] disabled:opacity-50 flex-1"
              >
                {creating ? "Creating..." : "Create Project"}
              </button>
              <button
                onClick={() => setShowModal(false)}
                disabled={creating}
                className="bg-[#cccccc] text-[#1a1a1a] font-medium px-6 py-2.5 rounded-lg text-base hover:bg-[#b3b3b3] disabled:opacity-50 flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectDashboard;

